import React from 'react';
import { Phone, Mail, MapPin, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PortalFooter() {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  
  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };
  
  return (
    <footer className="bg-slate-900 text-slate-300 mt-auto">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 py-10 md:py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="mb-4">
              <img 
                src="https://images.losspay.com/losspay/losspay-logo.png" 
                alt="LossPay" 
                className="h-8 brightness-0 invert opacity-90"
              />
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">
              LossPay Risk Technologies, Inc.<br />
              <span className="text-slate-500">d/b/a LossPay Insurance Agency</span><br />
              <span className="text-slate-500">d/b/a LossPay</span>
            </p>
            <p className="text-xs text-slate-500 mt-3 leading-relaxed">
              LossPay operates as a licensed insurance agency and managing general agency arranging coverage with duly licensed insurers.
            </p>
          </div>

          {/* Important Notice */}
          <div className="lg:col-span-1">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
              Important Notice
            </h4>
            <p className="text-xs text-slate-500 leading-relaxed">
              Insurance products may be issued through surplus lines carriers. Surplus lines insurers are not protected by state insurance guaranty funds. Policyholders are not covered by any guaranty fund in the event of insurer insolvency.
            </p>
          </div>

          {/* Coverage and Availability */}
          <div className="lg:col-span-1">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
              Coverage & Availability
            </h4>
            <p className="text-xs text-slate-500 leading-relaxed">
              Coverage is subject to the terms, conditions, limitations, and exclusions of the issued policy. Availability and eligibility may vary by state.
            </p>
          </div>

          {/* Policyholder Support */}
          <div className="lg:col-span-1">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
              Policyholder Support
            </h4>
            <div className="space-y-3">
              <a 
                href="tel:18335677729" 
                className="flex items-center gap-2.5 text-sm text-slate-300 hover:text-white transition-colors group"
              >
                <Phone className="h-4 w-4 text-slate-500 group-hover:text-blue-400 transition-colors" />
                <span>1-833-LOSSPAY</span>
              </a>
              <a 
                href="mailto:support@losspay.com" 
                className="flex items-center gap-2.5 text-sm text-slate-300 hover:text-white transition-colors group"
              >
                <Mail className="h-4 w-4 text-slate-500 group-hover:text-blue-400 transition-colors" />
                <span>support@losspay.com</span>
              </a>
              <div className="flex items-start gap-2.5 text-xs text-slate-500 pt-1">
                <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  16192 Coastal Highway<br />
                  Lewes, Delaware 19958
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Legal Links */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-4">
            <a 
              href="https://losspay.com/StateNotices"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-slate-400 hover:text-white transition-colors"
            >
              State Notices
            </a>
            <a 
              href="https://losspay.com/PrivacyPolicy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-slate-400 hover:text-white transition-colors"
            >
              Privacy Policy
            </a>
            <a 
              href="https://losspay.com/TermsOfUse"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-slate-400 hover:text-white transition-colors"
            >
              Terms of Service
            </a>
            <a 
              href="https://losspay.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-slate-400 hover:text-white transition-colors flex items-center gap-1"
            >
              losspay.com
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <p className="text-xs text-slate-500 text-center">
            © {currentYear} LossPay Risk Technologies, Inc. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
